"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Farnborough?",
  answer: "Yes. We regularly supply and erect scaffolding across Farnborough, North Camp, Cove, Southwood, and nearby Hampshire and Surrey border locations for domestic, commercial, and industrial sites."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Farnborough?",
  answer: "We provide independent access scaffolding, temporary roofs, edge protection, chimney stacks, birdcages, solar panel access towers, new-build structures, and bespoke commercial scaffolding designs."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We design and build secure roof access solutions including full perimeter edge protection, chimney scaffolding, dormer scaffolds, and temporary tin-hat coverings to keep properties weather-tight during major repairs."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We work directly with main contractors, roofing specialists, solar installers, local developers, and facilities managers, providing full handover certificates, method statements, and risk assessments."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our scaffolders are experienced in narrow alleys, tight boundaries, public footpaths, and awkward building layouts, using cantilevers, bridges, or beam work tailored to the property."
}, {
  id: "item-6",
  question: "How do I request a quote in Farnborough?",
  answer: "You can send project drawings, photos, or site details through our quote request form, or contact our team directly to arrange a site survey where we assess access requirements and measurements."
}];
const accreditations = [{
  name: "NASC Accredited"
}, {
  name: "CHAS Compliant"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="farnboroughfaq" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-12">
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center space-y-4 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <HelpCircle className="size-3.5 text-primary" />
                Frequently asked questions
              </Badge>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
              Farnborough scaffolding questions
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground">
              Straight answers to practical questions about our scaffolding setups across Farnborough and surrounding areas.
            </p>
          </motion.div>

          {/* Accordion Card */}
          <motion.div variants={itemVariants} className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, i) => <div data-index={i} key={faq.id}>
                  <AccordionItem value={faq.id} className="border-border py-1">
                    <AccordionTrigger className="text-left font-default text-base sm:text-lg font-semibold text-card-foreground hover:no-underline hover:text-primary transition-[color]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Bottom Card / CTA Banner */}
          <motion.div variants={itemVariants} className="rounded-xl bg-muted/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-1">
                {accreditations.map((acc, i) => <span data-index={i} key={acc.name} className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground bg-background px-2.5 py-1 rounded-md border border-border">
                    <ShieldCheck className="size-3 text-primary" />
                    {acc.name}
                  </span>)}
              </div>
              <h3 className="text-lg sm:text-xl font-semibold font-default text-foreground">
                Need a specific setup for your Farnborough site?
              </h3>
              <p className="text-sm text-muted-foreground max-w-xl">
                Tell us about your building, access points, and scope for a clear quotation.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/AreasWeCoverPage">
                  <span>View service areas</span>
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}